import React from 'react';
import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Filter,
  List,
  Lock,
  Plus,
  X,
} from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import './BarraDeTurnos.css';

/**
 * La barra de controles de Gestión de Turnos: filtros, vista y bloquear horario.
 *
 * Antes eran doce controles sueltos en una sola fila —seis filtros siempre
 * abiertos, tres botones de ícono y el botón de bloquear—. Sumando los anchos
 * mínimos, esa fila necesitaba ~1.700px de viewport para entrar; en cualquier
 * notebook arrancaba partida en dos o tres renglones, y como el bloque de la
 * derecha era `flex-shrink: 0` todo el faltante lo absorbían los filtros
 * envolviéndose. El punto más roto ni siquiera era el más chico: el panel
 * lateral libera sus 280px recién en 1024px, así que en 1025px quedaban 681px
 * útiles y en 1024px, 992px. Se veía mejor al achicar la ventana.
 *
 * El arreglo de fondo es que el ancho de la barra deje de ser proporcional a
 * la *cantidad de filtros que existen* (seis, fijo) y pase a serlo a los que
 * la persona aplicó (cero o uno, casi siempre): los filtros viven adentro de
 * un popover y en la barra quedan sólo los aplicados, como chips borrables.
 *
 * El popover es el de Radix (`ui/popover`) y no un div propio a propósito:
 * se renderiza en un portal, o sea *fuera* de `.dashboard-container`, que
 * apaga todo `transform` y toda `animation` con `!important`
 * (Dashboard.css:6869). Adentro del panel un desplegable escrito a mano
 * aparecería de golpe; acá abre y cierra normal, y encima trae foco, click
 * afuera y Escape sin escribirlos.
 */

const ETIQUETAS_ESTADO = {
  confirmed: 'Confirmados',
  pending: 'Pendientes',
  cancelled: 'Cancelados',
};

const ETIQUETAS_DIA = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

const ETIQUETAS_HORA = {
  morning: 'Mañana (6-12)',
  afternoon: 'Tarde (12-18)',
  evening: 'Noche (18-24)',
};

const FILTROS_VACIOS = {
  date: '',
  status: '',
  service: '',
  dayOfWeek: '',
  timeRange: '',
};

const formatearFecha = (fecha) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-AR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

export default function BarraDeTurnos({
  filtros,
  onCambiarFiltros,
  servicios = [],
  mostrarPasados,
  onCambiarMostrarPasados,
  vista,
  onCambiarVista,
  orden,
  onCambiarOrden,
  dias,
  onCambiarDias,
  onBloquearHorario,
  onAgregarTurno,
}) {
  const cambiar = (clave, valor) =>
    onCambiarFiltros((previos) => ({ ...previos, [clave]: valor }));

  // Un chip por filtro aplicado. La barra mide lo que la persona filtró, no la
  // cantidad de filtros que ofrece el panel.
  const chips = [];
  if (filtros.date) {
    chips.push({ clave: 'date', texto: formatearFecha(filtros.date) });
  }
  if (filtros.status) {
    chips.push({ clave: 'status', texto: ETIQUETAS_ESTADO[filtros.status] });
  }
  if (filtros.service) {
    chips.push({ clave: 'service', texto: filtros.service });
  }
  if (filtros.dayOfWeek) {
    chips.push({ clave: 'dayOfWeek', texto: ETIQUETAS_DIA[filtros.dayOfWeek] });
  }
  if (filtros.timeRange) {
    chips.push({ clave: 'timeRange', texto: ETIQUETAS_HORA[filtros.timeRange] });
  }
  if (mostrarPasados) {
    chips.push({ clave: 'pasados', texto: 'Incluye pasados' });
  }

  const borrarChip = (clave) => {
    if (clave === 'pasados') {
      onCambiarMostrarPasados(false);
      return;
    }
    cambiar(clave, '');
  };

  const limpiarTodo = () => {
    onCambiarFiltros(FILTROS_VACIOS);
    onCambiarMostrarPasados(false);
  };

  const ordenAscendente = orden === 'asc';

  return (
    /* La caja existe sólo para declarar el contenedor de las container
       queries: la barra no puede consultar su propio ancho. Ver BarraDeTurnos.css. */
    <div className="barra-turnos-caja">
      <div className="barra-turnos">
        <div className="barra-turnos__filtros">
          <Popover>
            <PopoverTrigger asChild>
              {/* El rótulo se esconde por CSS cuando la barra se angosta —es lo
                  que devuelve la barra a un solo renglón—, así que el nombre
                  accesible va en `aria-label` y no en el texto: si no, el botón
                  quedaría sin nombre justo en los anchos en que es un ícono
                  pelado. El `title` es la misma ayuda para quien ve. */}
              <button
                type="button"
                className={`barra-turnos__control ${chips.length > 0 ? 'es-activo' : ''}`}
                aria-label="Filtros"
                title="Filtros"
              >
                <Filter size={17} aria-hidden="true" />
                <span className="barra-turnos__control-texto">Filtros</span>
                {chips.length > 0 && (
                  <span className="barra-turnos__contador">{chips.length}</span>
                )}
              </button>
            </PopoverTrigger>

            <PopoverContent
              align="start"
              className="barra-turnos-panel w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl p-0"
            >
              <div className="barra-turnos-panel__cuerpo">
                <label className="barra-turnos-panel__fila">
                  <span>Fecha</span>
                  <input
                    type="date"
                    value={filtros.date}
                    onChange={(e) => cambiar('date', e.target.value)}
                  />
                </label>

                <label className="barra-turnos-panel__fila">
                  <span>Estado</span>
                  <select
                    value={filtros.status}
                    onChange={(e) => cambiar('status', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {Object.entries(ETIQUETAS_ESTADO).map(([valor, texto]) => (
                      <option key={valor} value={valor}>
                        {texto}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="barra-turnos-panel__fila">
                  <span>Servicio</span>
                  <select
                    value={filtros.service || ''}
                    onChange={(e) => cambiar('service', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {servicios.map((servicio) => (
                      <option key={servicio.id} value={servicio.name}>
                        {servicio.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="barra-turnos-panel__fila">
                  <span>Día de semana</span>
                  <select
                    value={filtros.dayOfWeek || ''}
                    onChange={(e) => cambiar('dayOfWeek', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {Object.entries(ETIQUETAS_DIA).map(([valor, texto]) => (
                      <option key={valor} value={valor}>
                        {texto}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="barra-turnos-panel__fila">
                  <span>Hora</span>
                  <select
                    value={filtros.timeRange || ''}
                    onChange={(e) => cambiar('timeRange', e.target.value)}
                  >
                    <option value="">Todas</option>
                    {Object.entries(ETIQUETAS_HORA).map(([valor, texto]) => (
                      <option key={valor} value={valor}>
                        {texto}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="barra-turnos-panel__casilla">
                  <input
                    type="checkbox"
                    checked={mostrarPasados}
                    onChange={(e) => onCambiarMostrarPasados(e.target.checked)}
                  />
                  <span>Incluir turnos pasados</span>
                </label>
              </div>

              {chips.length > 0 && (
                <div className="barra-turnos-panel__pie">
                  <button type="button" onClick={limpiarTodo}>
                    Limpiar todo
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {chips.map((chip) => (
            <span key={chip.clave} className="barra-turnos__chip">
              <span>{chip.texto}</span>
              <button
                type="button"
                onClick={() => borrarChip(chip.clave)}
                aria-label={`Quitar filtro ${chip.texto}`}
              >
                <X size={13} aria-hidden="true" />
              </button>
            </span>
          ))}

          {chips.length > 1 && (
            <button
              type="button"
              className="barra-turnos__limpiar"
              onClick={limpiarTodo}
            >
              Limpiar todo
            </button>
          )}
        </div>

        <div className="barra-turnos__acciones">
          {/* Este hueco es el control propio de cada vista: el orden en la lista,
              el rango de días en el calendario. Va primero, y no al lado de
              Bloquear horario, porque el grupo está anclado a la derecha con
              `margin-left: auto` y crece hacia la izquierda: puesto acá, al
              cambiar de vista se reemplaza uno por otro en el borde izquierdo y
              ni el selector de vista ni Bloquear horario se mueven de lugar. */}
          {vista === 'list' ? (
            /* El orden sólo tiene sentido en la lista: en el calendario los
               turnos se agrupan por día, así que invertirlo reordena adentro de
               cada columna y desde afuera parece que el botón no hizo nada. */
            <button
              type="button"
              className="barra-turnos__control"
              onClick={() => onCambiarOrden(ordenAscendente ? 'desc' : 'asc')}
            >
              {ordenAscendente ? (
                <ArrowUp size={17} aria-hidden="true" />
              ) : (
                <ArrowDown size={17} aria-hidden="true" />
              )}
              {ordenAscendente ? 'Próximos primero' : 'Más lejanos primero'}
            </button>
          ) : (
            <div
              className="barra-turnos__vista barra-turnos__dias"
              role="group"
              aria-label="Cantidad de días del calendario"
            >
              <span>Días</span>
              {[4, 5, 7].map((cantidad) => (
                <button
                  key={cantidad}
                  type="button"
                  onClick={() => onCambiarDias(cantidad)}
                  aria-pressed={dias === cantidad}
                >
                  {cantidad}
                </button>
              ))}
            </div>
          )}

          {/* Selector de vista: dos opciones excluyentes, con la palabra al lado
              del ícono. Antes eran 📋 y 📅 sueltos adentro de la misma cápsula
              que el botón de orden, así que no había forma de ver que los dos
              primeros eran "en qué formato miro" y el tercero "en qué orden". */}
          <div
            className="barra-turnos__vista"
            role="group"
            aria-label="Formato de la vista"
          >
            <button
              type="button"
              onClick={() => onCambiarVista('list')}
              aria-pressed={vista === 'list'}
            >
              <List size={17} aria-hidden="true" />
              Lista
            </button>
            <button
              type="button"
              onClick={() => onCambiarVista('calendar')}
              aria-pressed={vista === 'calendar'}
            >
              <CalendarDays size={17} aria-hidden="true" />
              Calendario
            </button>
          </div>

          {/* Dos acciones separadas y no una con un menú adentro: anotar un turno
              que llegó por teléfono y cerrar un horario son cosas distintas —una
              crea un turno con cliente, la otra marca que algo no está
              disponible— y meterlas en el mismo botón obligaba a elegir de qué se
              trataba antes de ver la pantalla. Bloquear va en secundario porque es
              la menos frecuente de las dos. */}
          <button
            type="button"
            className="barra-turnos__bloquear"
            onClick={onBloquearHorario}
          >
            <Lock size={17} aria-hidden="true" />
            Bloquear horario
          </button>

          <button
            type="button"
            className="barra-turnos__agregar-turno"
            onClick={onAgregarTurno}
          >
            <Plus size={17} aria-hidden="true" />
            Agregar turno
          </button>
        </div>
      </div>
    </div>
  );
}
